import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BrandLockup } from "@/components/LogoMark";
import { toast } from "sonner";
import { api, errMsg, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ArbitratorInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, login, loading } = useAuth();
  const [inv, setInv] = useState(null);
  const [fatal, setFatal] = useState("");
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get(`/arbitrator-invites/by-token/${token}`)
      .then(({ data }) => setInv(data))
      .catch((e) => setFatal(errMsg(e)));
  }, [token]);

  const respond = async (action) => {
    setBusy(action);
    setError("");
    try {
      await api.post(`/arbitrator-invites/${inv.id}/respond`, { action });
      toast.success(action === "accept" ? "Reference accepted" : "Reference declined");
      navigate(action === "accept" ? `/cases/${inv.case_id}` : "/dashboard");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const signIn = async (e) => {
    e.preventDefault();
    setBusy("login");
    setError("");
    try {
      await login(inv.arbitrator_email, password);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md" data-testid="arbitrator-invite-page">
        <BrandLockup />

        {fatal ? (
          <p className="mt-8 text-sm text-red-800 dark:text-red-400" data-testid="arb-invite-invalid">{fatal}</p>
        ) : !inv || loading ? (
          <p className="label-caps mt-8">Opening request…</p>
        ) : (
          <>
            <p className="label-caps mt-8">Request to act as arbitrator</p>
            <h1 className="font-display text-3xl mt-2" data-testid="arb-invite-title">{inv.case_title}</h1>
            <p className="font-mono text-xs text-muted-foreground mt-2">
              {inv.case_number || inv.case_ref}
            </p>
            <p className="text-sm text-muted-foreground mt-3">
              {inv.claimant} <span className="mx-2 text-blue-900 dark:text-blue-400">versus</span> {inv.respondent}
            </p>
            <dl className="mt-5 space-y-2 text-sm">
              <div className="flex justify-between gap-6">
                <dt className="text-muted-foreground">Requested by</dt><dd>{inv.invited_by_name}</dd>
              </div>
              {inv.seat && (
                <div className="flex justify-between gap-6">
                  <dt className="text-muted-foreground">Seat</dt><dd>{inv.seat}</dd>
                </div>
              )}
              {inv.claim_amount != null && (
                <div className="flex justify-between gap-6">
                  <dt className="text-muted-foreground">Claim</dt>
                  <dd>{money(inv.claim_amount, inv.currency)}</dd>
                </div>
              )}
            </dl>

            {inv.status !== "pending" || inv.expired ? (
              <p className="mt-8 text-sm text-red-800 dark:text-red-400" data-testid="arb-invite-closed">
                {inv.expired && inv.status === "pending"
                  ? "This request has lapsed. Ask counsel for a fresh one."
                  : `This request is already ${inv.status}.`}
              </p>
            ) : user && user.role === "arbitrator" ? (
              <div className="mt-8 space-y-4">
                {error && <p className="text-sm text-red-800 dark:text-red-400" data-testid="arb-invite-error">{error}</p>}
                <div className="flex flex-wrap gap-3">
                  <Button onClick={() => respond("accept")} disabled={!!busy} data-testid="arb-invite-accept"
                    className="rounded-none bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs h-11 px-6">
                    {busy === "accept" ? "Accepting…" : "Accept reference"}
                  </Button>
                  <Button onClick={() => respond("decline")} disabled={!!busy} data-testid="arb-invite-decline"
                    className="rounded-none bg-red-800 hover:bg-red-700 text-white uppercase tracking-widest text-xs h-11 px-6">
                    {busy === "decline" ? "Declining…" : "Decline"}
                  </Button>
                </div>
              </div>
            ) : user ? (
              <p className="mt-8 text-sm text-red-800 dark:text-red-400" data-testid="arb-invite-wrong-role">
                This request must be answered from the arbitrator's profile it was addressed to.
              </p>
            ) : (
              <form onSubmit={signIn} className="mt-8 space-y-5" data-testid="arb-invite-login-form">
                <div className="space-y-2">
                  <Label className="label-caps">Arbitrator's email</Label>
                  <Input data-testid="arb-invite-email" value={inv.arbitrator_email || ""} readOnly
                    className="rounded-none bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label className="label-caps">Password</Label>
                  <Input data-testid="arb-invite-password" type="password" required value={password}
                    onChange={(e) => setPassword(e.target.value)} className="rounded-none focus:ring-2 focus:ring-blue-800" />
                </div>
                {error && <p className="text-sm text-red-800 dark:text-red-400" data-testid="arb-invite-error">{error}</p>}
                <Button type="submit" disabled={!!busy} data-testid="arb-invite-signin"
                  className="w-full rounded-none bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs h-11">
                  {busy === "login" ? "Please wait…" : "Sign in to respond"}
                </Button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
